import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ProjectFormFields } from '../../components/projects/ProjectFormFields';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { DuplicateIconButton } from '../../components/ui/DuplicateIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { InlineAlert } from '../../components/ui/InlineAlert';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PageShortcutLink } from '../../components/ui/PageShortcutLink';
import { SelectionContextMeta } from '../../components/ui/SelectionContextMeta';
import { ViewIconLink } from '../../components/ui/ViewIconLink';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { Contract, listContracts } from '../../services/commercialContractsApi';
import {
  createProject,
  deleteProject,
  duplicateProject,
  listProjectClients,
  listProjects,
  Project,
  ProjectClientSummary,
  ProjectUpsertPayload,
  updateProject,
} from '../../services/commercialProjectsApi';
import { formatDateBr } from '../../utils/formatDateBr';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  emptyProjectForm,
  formatProjectDate,
  normalizeProjectPayload,
  PROJECT_STATUS_LABELS,
  projectToForm,
} from '../../utils/projectFormUtils';

type LifecycleFilter = '' | 'active' | 'cancelled' | 'closed';

const LIFECYCLE_OPTIONS: { id: LifecycleFilter; label: string }[] = [
  { id: '', label: 'Todos' },
  { id: 'active', label: 'Ativos' },
  { id: 'cancelled', label: 'Cancelados' },
  { id: 'closed', label: 'Encerrados' },
];

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  Draft: 'Rascunho',
  Active: 'Ativo',
  Suspended: 'Suspenso',
  Expired: 'Expirado',
  Cancelled: 'Cancelado',
};

function formatCurrencyBr(value?: number | null): string | null {
  if (value == null) return null;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDaysRemaining(days?: number | null): string | null {
  if (days == null) return null;
  if (days < 0) return `encerrado há ${Math.abs(days)} d`;
  if (days === 0) return 'encerra hoje';
  return `${days} d restantes`;
}

function formatProjectExtra(project: Project): string | null {
  const parts: string[] = [];
  if (project.ownerUserName) parts.push(project.ownerUserName);
  if (project.sizeEstimate != null) {
    parts.push(`${project.sizeEstimate}${project.sizeUnit ? ` ${project.sizeUnit}` : ''}`);
  }
  if (project.effortEstimateHours != null) {
    parts.push(`${project.effortEstimateHours}h`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function ProjectsPage() {
  const { canWriteModule, canDeleteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('commercial.projects');
  const canDelete = canDeleteModule('commercial.projects');
  const [searchParams, setSearchParams] = useSearchParams();
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();

  const [lifecycle, setLifecycle] = useState<LifecycleFilter>('active');
  const [clientSearch, setClientSearch] = useState('');
  const [clients, setClients] = useState<ProjectClientSummary[]>([]);
  const [clientTotal, setClientTotal] = useState(0);
  const [clientPage, setClientPage] = useState(1);
  const [selectedCompanyId, setSelectedCompanyId] = useState(searchParams.get('companyId') ?? '');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedContractId, setSelectedContractId] = useState(searchParams.get('contractId') ?? '');
  const [projects, setProjects] = useState<Project[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProjectUpsertPayload>(emptyProjectForm(selectedContractId));
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const pageSize = 50;
  const clientPages = Math.max(1, Math.ceil(clientTotal / pageSize));

  const selectedClient = useMemo(
    () => clients.find((item) => item.companyId === selectedCompanyId) ?? null,
    [clients, selectedCompanyId],
  );

  const selectedContract = useMemo(
    () => contracts.find((item) => item.id === selectedContractId) ?? null,
    [contracts, selectedContractId],
  );

  const selectionContextItems = useMemo(() => {
    const slaParts = [selectedContract?.responseSlaName, selectedContract?.resolutionSlaName].filter(Boolean);
    const projectCountLabel = selectedContractId
      ? `${projects.length} projeto${projects.length !== 1 ? 's' : ''}`
      : null;

    return [
      { label: 'Cliente', value: selectedClient?.companyName ?? selectedContract?.companyName },
      { label: 'Documento', value: selectedClient?.document },
      {
        label: 'Contratos',
        value:
          !selectedContract && selectedClient
            ? `${selectedClient.contractCount} contrato${selectedClient.contractCount !== 1 ? 's' : ''}`
            : null,
      },
      {
        label: 'Contrato',
        value: selectedContract ? `${selectedContract.contractCode} — ${selectedContract.title}` : null,
      },
      {
        label: 'Status',
        value: selectedContract
          ? (CONTRACT_STATUS_LABELS[selectedContract.status] ?? selectedContract.status)
          : null,
      },
      {
        label: 'Vigência',
        value: selectedContract
          ? `${formatDateBr(selectedContract.startDateUtc)} – ${formatDateBr(selectedContract.endDateUtc)}`
          : null,
      },
      { label: 'Prazo', value: formatDaysRemaining(selectedContract?.daysUntilEnd) },
      { label: 'Produto', value: selectedContract?.salesProductName },
      { label: 'SLAs', value: slaParts.length > 0 ? slaParts.join(' / ') : null },
      { label: 'Calendário', value: selectedContract?.calendarName },
      { label: 'Responsável', value: selectedContract?.ownerUserName },
      { label: 'Valor', value: formatCurrencyBr(selectedContract?.amount) },
      { label: 'Projetos', value: projectCountLabel },
    ];
  }, [projects.length, selectedClient, selectedContract, selectedContractId]);

  const syncQuery = useCallback(
    (next: { companyId?: string; contractId?: string }) => {
      const params = new URLSearchParams();
      const companyId = next.companyId ?? selectedCompanyId;
      const contractId = next.contractId ?? selectedContractId;
      if (companyId) params.set('companyId', companyId);
      if (contractId) params.set('contractId', contractId);
      setSearchParams(params, { replace: true });
    },
    [selectedCompanyId, selectedContractId, setSearchParams],
  );

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const result = await listProjectClients({
        lifecycle: lifecycle || undefined,
        search: clientSearch.trim() || undefined,
        page: clientPage,
        pageSize,
      });
      setClients(result.items);
      setClientTotal(result.totalCount);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar clientes.'));
    } finally {
      setLoadingClients(false);
    }
  }, [lifecycle, clientSearch, clientPage]);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setContracts([]);
      return;
    }

    setLoadingContracts(true);
    void listContracts({ companyId: selectedCompanyId, pageSize: 100 })
      .then((result) => setContracts(result.items))
      .catch((error) => toast.error(getRequestErrorMessage(error, 'Falha ao carregar contratos.')))
      .finally(() => setLoadingContracts(false));
  }, [selectedCompanyId]);

  const loadProjects = useCallback(async () => {
    if (!selectedContractId) {
      setProjects([]);
      return;
    }

    setLoadingProjects(true);
    try {
      const result = await listProjects({ contractId: selectedContractId, pageSize: 100 });
      setProjects(result.items);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar projetos.'));
    } finally {
      setLoadingProjects(false);
    }
  }, [selectedContractId]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyProjectForm(selectedContractId));
  }

  function startEdit(project: Project): void {
    expandForm();
    setEditingId(project.id);
    setForm(projectToForm(project));
  }

  function buildDetailLink(projectId: string): string {
    const params = new URLSearchParams();
    if (selectedCompanyId) params.set('companyId', selectedCompanyId);
    if (selectedContractId) params.set('contractId', selectedContractId);
    const query = params.toString();
    return `/commercial/projects/${projectId}${query ? `?${query}` : ''}`;
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !selectedContractId) return;

    setSaving(true);
    try {
      const payload = normalizeProjectPayload(form, selectedContractId);

      if (editingId) {
        await updateProject(editingId, payload);
        toast.success('Projeto atualizado.');
        collapseForm(resetForm);
      } else {
        await createProject(payload);
        toast.success('Projeto criado.');
        handleCreateSuccess(resetForm);
      }
      await loadProjects();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar projeto.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDuplicate(project: Project): Promise<void> {
    if (
      !canWrite
      || !(await confirm({
        title: 'Duplicar projeto',
        message: `Duplicar "${project.name}"?`,
        confirmLabel: 'Duplicar',
      }))
    ) {
      return;
    }

    setDuplicatingId(project.id);
    try {
      await duplicateProject(project.id);
      toast.success('Projeto duplicado.');
      await loadProjects();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao duplicar projeto.'));
    } finally {
      setDuplicatingId(null);
    }
  }

  async function handleDelete(project: Project): Promise<void> {
    if (
      !canDelete
      || !(await confirm({
        title: 'Excluir projeto',
        message: `Excluir "${project.name}"?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    setDeletingId(project.id);
    try {
      await deleteProject(project.id);
      toast.success('Projeto excluído.');
      if (editingId === project.id) {
        collapseForm(resetForm);
      }
      await loadProjects();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir projeto.'));
    } finally {
      setDeletingId(null);
    }
  }

  function handleCompanyChange(companyId: string): void {
    setSelectedCompanyId(companyId);
    setSelectedContractId('');
    collapseForm(resetForm);
    syncQuery({ companyId, contractId: '' });
  }

  function handleContractChange(contractId: string): void {
    setSelectedContractId(contractId);
    setForm(emptyProjectForm(contractId));
    collapseForm(resetForm);
    syncQuery({ contractId });
  }

  return (
    <PageShell>
      <PageHeader
        title="Projetos"
        description="Gestão de projetos de contratos de clientes."
        actions={
          <PageShortcutLink to="/commercial/contracts" prefix="Acesso rápido">
            Contratos
          </PageShortcutLink>
        }
      />

      <section className="mb-4 rounded-xl border border-secondary-200 bg-white p-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="erp-label min-w-[120px]">
            Ciclo de vida
            <select
              className="erp-input mt-1"
              value={lifecycle}
              onChange={(event) => {
                setLifecycle(event.target.value as LifecycleFilter);
                setClientPage(1);
              }}
            >
              {LIFECYCLE_OPTIONS.map((option) => (
                <option key={option.id || 'all'} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="erp-label min-w-[160px]">
            Buscar cliente
            <input
              className="erp-input mt-1"
              value={clientSearch}
              onChange={(event) => {
                setClientSearch(event.target.value);
                setClientPage(1);
              }}
              placeholder="Nome ou documento..."
            />
          </label>

          <label className="erp-label min-w-[220px] flex-1">
            Cliente
            {loadingClients ? (
              <div className="mt-1">
                <LoadingSpinner />
              </div>
            ) : (
              <select
                className="erp-input mt-1"
                value={selectedCompanyId}
                onChange={(event) => handleCompanyChange(event.target.value)}
              >
                <option value="">Selecione um cliente</option>
                {clients.map((client) => (
                  <option key={client.companyId} value={client.companyId}>
                    {client.companyName} ({client.contractCount} contrato
                    {client.contractCount !== 1 ? 's' : ''})
                  </option>
                ))}
              </select>
            )}
          </label>

          <label className="erp-label min-w-[220px] flex-1">
            Contrato
            {loadingContracts ? (
              <div className="mt-1">
                <LoadingSpinner />
              </div>
            ) : (
              <select
                className="erp-input mt-1"
                value={selectedContractId}
                onChange={(event) => handleContractChange(event.target.value)}
                disabled={!selectedCompanyId}
              >
                <option value="">
                  {selectedCompanyId ? 'Selecione um contrato' : 'Selecione um cliente primeiro'}
                </option>
                {contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contractCode} — {contract.title}
                  </option>
                ))}
              </select>
            )}
          </label>
        </div>

        {clientPages > 1 ? (
          <div className="mt-2">
            <ListPaginationBar
              totalCount={clientTotal}
              page={clientPage}
              pageSize={pageSize}
              onPrevious={() => setClientPage((current) => Math.max(1, current - 1))}
              onNext={() => setClientPage((current) => Math.min(clientPages, current + 1))}
            />
          </div>
        ) : null}
      </section>

      {canWrite && selectedContractId ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar projeto' : 'Novo projeto'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.name || 'projeto selecionado'} — o formulário abriu em uma janela` : undefined
          }
          createSummary="Clique para cadastrar um novo projeto"
          isEditing={!!editingId}
          panelClassName="w-full max-w-5xl"
        >
          <form className="px-4 pb-4 pt-3" onSubmit={(event) => void handleSubmit(event)}>
            <ProjectFormFields form={form} canWrite={canWrite} onChange={setForm} />
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <section className="mb-4 rounded-xl border border-secondary-200 bg-white p-3">
        <SelectionContextMeta items={selectionContextItems} />

        {!selectedContractId ? (
          <InlineAlert>
            {!selectedCompanyId
              ? 'Selecione um cliente e um contrato para ver os projetos.'
              : 'Selecione um contrato para ver os projetos.'}
          </InlineAlert>
        ) : loadingProjects ? (
          <LoadingSpinner label="Carregando projetos..." />
        ) : projects.length === 0 ? (
          <InlineAlert>
            Nenhum projeto neste contrato. {canWrite ? 'Use Novo projeto acima para cadastrar o primeiro.' : ''}
          </InlineAlert>
        ) : (
          <div className="erp-card overflow-x-auto">
            <table className="erp-table w-full">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th className="w-48">Ações</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  const extra = formatProjectExtra(project);
                  return (
                    <tr key={project.id}>
                      <td className="whitespace-nowrap font-mono text-sm">{project.code || '—'}</td>
                      <td className="font-medium text-secondary-900">
                        {project.name}
                        {extra ? (
                          <p className="mt-0.5 text-xs font-normal text-secondary-500">{extra}</p>
                        ) : null}
                      </td>
                      <td>
                        <CodedStatusBadge
                          status={project.status}
                          label={PROJECT_STATUS_LABELS[project.status] ?? project.status}
                        />
                      </td>
                      <td className="whitespace-nowrap text-sm">
                        {formatProjectDate(project.plannedStartDateUtc)}
                      </td>
                      <td className="whitespace-nowrap text-sm">
                        {formatProjectDate(project.plannedEndDateUtc)}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <ViewIconLink
                            to={buildDetailLink(project.id)}
                            label="Detalhes do projeto"
                          />
                          {canWrite ? (
                            <EditIconButton
                              label="Editar projeto"
                              onClick={() => startEdit(project)}
                            />
                          ) : null}
                          {canWrite ? (
                            <DuplicateIconButton
                              label={duplicatingId === project.id ? 'Duplicando...' : 'Duplicar projeto'}
                              disabled={duplicatingId === project.id || deletingId === project.id}
                              onClick={() => void handleDuplicate(project)}
                            />
                          ) : null}
                          {canDelete ? (
                            <DeleteIconButton
                              label={deletingId === project.id ? 'Excluindo...' : 'Excluir projeto'}
                              disabled={deletingId === project.id || duplicatingId === project.id}
                              onClick={() => void handleDelete(project)}
                            />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PageShell>
  );
}
